import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, MapPin, ArrowRight, Sparkles, Send } from 'lucide-react';
import { api } from '../services/api';
import InscricaoEventoModal from './InscricaoEventoModal';

/**
 * Widget "Próximas ações das ONGs" — exibe as N ações futuras mais próximas
 * de todas as ONGs do município.
 *
 * Endpoint: GET /api/v2/coletivos/proximas-acoes?municipio_slug=<slug>&limit=<n>
 */
export default function ProximasAcoesWidget({ municipioSlug, limit = 3 }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const params = { limit };
    if (municipioSlug) params.municipio_slug = municipioSlug;
    api.get('/v2/coletivos/proximas-acoes', { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [municipioSlug, limit]);

  if (!loaded || items.length === 0) return null;

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'short', weekday: 'short',
      });
    } catch { return iso; }
  };

  return (
    <section className="my-10" data-testid="proximas-acoes-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <CalendarClock className="w-6 h-6 text-[var(--primary)]" />
            Próximas ações das ONGs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Eventos e mobilizações abertas ao público nos próximos dias
          </p>
        </div>
        <Link
          to={municipioSlug ? `/${municipioSlug}/coletivos?tipo=ong` : '/coletivos?tipo=ong'}
          className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:opacity-80"
          data-testid="proximas-acoes-ver-todas"
        >
          Ver ONGs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((ev) => (
          <div
            key={ev.evento_id}
            data-testid={`proxima-acao-card-${ev.evento_id}`}
            className="group relative flex flex-col rounded-xl overflow-hidden bg-white border border-gray-100 hover:border-[var(--primary)]/40 hover:shadow-md transition"
          >
            <Link
              to={municipioSlug ? `/${municipioSlug}/coletivos/${ev.coletivo_slug}` : `/coletivos/${ev.coletivo_slug}`}
              className="block"
            >
              {/* Cabeçalho com foto do coletivo + data */}
              <div className="relative h-24 bg-gradient-to-br from-[var(--primary)] to-purple-900 overflow-hidden">
                {ev.coletivo_foto ? (
                  <img
                    src={ev.coletivo_foto}
                    alt={ev.coletivo_nome}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition"
                    loading="lazy"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-[var(--primary)] shadow-sm">
                  <Sparkles className="w-3 h-3" />
                  {formatDate(ev.data_inicio)}
                </div>
                <div className="absolute bottom-2 left-3 right-3">
                  <p className="text-white text-xs font-medium truncate drop-shadow">
                    {ev.coletivo_nome}
                  </p>
                </div>
              </div>

              {/* Corpo */}
              <div className="p-4 pb-2">
                <h3 className="font-bold text-base text-gray-900 mb-1 line-clamp-2 group-hover:text-[var(--primary)] transition">
                  {ev.titulo}
                </h3>
                {ev.descricao ? (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {ev.descricao}
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5 text-xs text-gray-500">
                  {ev.hora_inicio ? (
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="w-3.5 h-3.5" />
                      {ev.hora_inicio}{ev.hora_fim ? ` – ${ev.hora_fim}` : ''}
                    </span>
                  ) : null}
                  {ev.local ? (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {ev.local}
                    </span>
                  ) : null}
                </div>
              </div>
            </Link>

            {/* CTA Inscrever-se */}
            <div className="mt-auto px-4 pt-2 pb-4">
              <button
                type="button"
                onClick={() => setSelectedEvent(ev)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--primary)] py-2 px-3 text-sm font-semibold text-white hover:opacity-90 transition"
                data-testid={`proxima-acao-inscrever-${ev.evento_id}`}
              >
                <Send className="w-4 h-4" />
                Inscrever-se
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedEvent ? (
        <InscricaoEventoModal
          evento={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      ) : null}
    </section>
  );
}
